/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2023-2025. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: skeletonNativeDirective.ts
 * Created by: chan.ying
 * Create Date: 2025-06-11 13:41
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import type {App, DirectiveBinding} from 'vue';

interface ISkeletonOption {
    rows?: number;
    radius?: number;
    bg?: string;
}

export default {
    install: (Vue: App, opts?: ISkeletonOption) => {
        opts ??= {rows: 10, radius: 5, bg: 'var(--surface-container-high)'};
        Vue.directive('skeleton', {
            updated(el: HTMLElement, binding: DirectiveBinding) {
                let {value} = binding;
                if (typeof value !== 'object') {
                    value = {loading: value};
                }
                const {loading} = value;
                const rows = value.rows ?? opts?.rows ?? 10;
                const bg = value.bg ?? opts?.bg ?? 'var(--surface-container-high)';
                const radius = value.radius ?? opts?.radius ?? 5;

                if (el.tagName !== 'TABLE') return;

                // generate skeleton
                if (loading && (!el.dataset.skeleton || el.dataset.skeleton === '0')) {
                    el.dataset.skeleton = '1';

                    // hide tbody content
                    const tbodies = el.querySelectorAll('tbody');
                    tbodies.forEach(tbody => {
                        (tbody as HTMLElement).style.display = 'none';
                    });

                    // get col count
                    let colCount = 0;
                    const thead = el.querySelector('thead');
                    if (thead) {
                        const ths = thead.querySelectorAll('th');
                        colCount = ths.length;
                    } else {
                        // fallback: get first col
                        const firstTr = el.querySelector('tbody tr');
                        if (firstTr) {
                            colCount = firstTr.children.length;
                        }
                    }
                    if (!colCount) colCount = 3; // fallback

                    // create skeleton tbody
                    let skeletonTbody = el.querySelector('tbody.skeleton-tbody') as HTMLElement;
                    if (!skeletonTbody) {
                        skeletonTbody = document.createElement('tbody');
                        skeletonTbody.className = 'skeleton-tbody';
                        el.appendChild(skeletonTbody);
                    }
                    skeletonTbody.innerHTML = '';
                    for (let i = 0; i < rows; i++) {
                        const tr = document.createElement('tr');
                        tr.className = 'skeleton-tr';
                        for (let j = 0; j < colCount; j++) {
                            const td = document.createElement('td');
                            const div = document.createElement('div');
                            div.className = 'ztn-skeleton-loading';
                            div.style.height = '16px';
                            div.style.margin = '8px 0';
                            div.style.background = bg;
                            div.style.borderRadius = `${radius}px`;
                            div.style.width = `${Math.random() * 60 + 30}%`;
                            div.style.textIndent = '-999px';
                            div.appendChild(document.createTextNode(''));
                            td.appendChild(div);
                            tr.appendChild(td);
                        }
                        skeletonTbody.appendChild(tr);
                    }
                }
                // remove skeleton
                else if (!loading && el.dataset.skeleton === '1') {
                    el.dataset.skeleton = '0';
                    // remove skeleton tbody
                    const skeletonTbody = el.querySelector('tbody.skeleton-tbody');
                    if (skeletonTbody) {
                        el.removeChild(skeletonTbody);
                    }
                    // restore the original tbody
                    const tbodies = el.querySelectorAll('tbody');
                    tbodies.forEach(tbody => {
                        if (!(tbody as HTMLElement).classList.contains('skeleton-tbody')) {
                            (tbody as HTMLElement).style.display = '';
                        }
                    });
                }
            }
        });
    }
}